import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { mockClients, mockProjects } from '@/mocks/data';

function NewMeetingContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '10:00',
    clientId: searchParams.get('clientId') || '',
    projectId: searchParams.get('projectId') || '',
    participants: '',
    agenda: '',
    duration: '60',
    location: '',
    meetingType: 'client' as 'client' | 'internal' | 'planning',
    recurring: false,
    reminderEnabled: true,
    reminderTime: '15',
  });

  const filteredProjects = formData.clientId
    ? mockProjects.filter((p) => p.clientId === formData.clientId)
    : mockProjects;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Creating meeting:', formData);
    navigate('/meetings');
  };

  const generateMeetingLink = () => {
    const links = [
      'https://zoom.us/j/123456789',
      'https://meet.google.com/xyz-abc-def',
      'https://teams.microsoft.com/l/meetup-join/...',
    ];
    return links[Math.floor(Math.random() * links.length)];
  };

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Meetings', href: '/meetings' }, { label: 'New Meeting' }]} />

      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Schedule New Meeting</h1>
        <p className="text-gray-600 mb-8">Create a new meeting and invite participants</p>

        <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4 space-y-6">
          {/* Meeting Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type *</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, meetingType: 'client' })}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  formData.meetingType === 'client'
                    ? 'border-flowky-primary bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-center">
                  <span className="text-2xl mb-1 block">👥</span>
                  <span className="text-sm font-medium">Client Meeting</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, meetingType: 'internal' })}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  formData.meetingType === 'internal'
                    ? 'border-flowky-primary bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-center">
                  <span className="text-2xl mb-1 block">🏢</span>
                  <span className="text-sm font-medium">Internal</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, meetingType: 'planning' })}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  formData.meetingType === 'planning'
                    ? 'border-flowky-primary bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-center">
                  <span className="text-2xl mb-1 block">📋</span>
                  <span className="text-sm font-medium">Planning</span>
                </div>
              </button>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                id="date"
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                Time *
              </label>
              <input
                id="time"
                type="time"
                required
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes)
              </label>
              <select
                id="duration"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>

          {/* Client and Project */}
          {formData.meetingType === 'client' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                  Client *
                </label>
                <select
                  id="clientId"
                  required
                  value={formData.clientId}
                  onChange={(e) => {
                    setFormData({ ...formData, clientId: e.target.value, projectId: '' });
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="">Select client</option>
                  {mockClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
                  Project (Optional)
                </label>
                <select
                  id="projectId"
                  value={formData.projectId}
                  onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="">No specific project</option>
                  {filteredProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Participants */}
          <div>
            <label htmlFor="participants" className="block text-sm font-medium text-gray-700 mb-1">
              Participants *
            </label>
            <input
              id="participants"
              type="text"
              required
              value={formData.participants}
              onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="john@example.com, jane@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">Separate email addresses with commas</p>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location / Meeting Link
            </label>
            <div className="flex gap-2">
              <input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="Conference room or video call link"
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, location: generateMeetingLink() })}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors text-sm"
              >
                Generate Link
              </button>
            </div>
          </div>

          {/* Agenda */}
          <div>
            <label htmlFor="agenda" className="block text-sm font-medium text-gray-700 mb-1">
              Agenda / Description
            </label>
            <textarea
              id="agenda"
              value={formData.agenda}
              onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Meeting agenda, topics to discuss, or description..."
            />
          </div>

          {/* Meeting Options */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Meeting Options</h3>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.recurring}
                onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                className="h-4 w-4 text-flowky-primary focus:ring-flowky-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Recurring meeting (weekly at same time)
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.reminderEnabled}
                onChange={(e) => setFormData({ ...formData, reminderEnabled: e.target.checked })}
                className="h-4 w-4 text-flowky-primary focus:ring-flowky-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Send reminder emails</span>
            </label>

            {formData.reminderEnabled && (
              <div className="ml-6">
                <label
                  htmlFor="reminderTime"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reminder time
                </label>
                <select
                  id="reminderTime"
                  value={formData.reminderTime}
                  onChange={(e) => setFormData({ ...formData, reminderTime: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="5">5 minutes before</option>
                  <option value="15">15 minutes before</option>
                  <option value="30">30 minutes before</option>
                  <option value="60">1 hour before</option>
                  <option value="1440">1 day before</option>
                </select>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="px-6 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
            >
              Schedule Meeting
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors"
            >
              Cancel
            </button>
          </div>

          {/* Preview */}
          {formData.date && formData.time && formData.participants && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Meeting Preview</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p>
                  📅{' '}
                  {new Date(`${formData.date}T${formData.time}`).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}{' '}
                  at {formData.time}
                </p>
                <p>⏱️ Duration: {formData.duration} minutes</p>
                <p>👥 Participants: {formData.participants}</p>
                {formData.location && <p>📍 Location: {formData.location}</p>}
                {formData.clientId && (
                  <p>🏢 Client: {mockClients.find((c) => c.id === formData.clientId)?.name}</p>
                )}
                {formData.projectId && (
                  <p>📋 Project: {mockProjects.find((p) => p.id === formData.projectId)?.name}</p>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </AppLayout>
  );
}

export default function NewMeetingPage() {
  return (
    <ProtectedRoute>
      <NewMeetingContent />
    </ProtectedRoute>
  );
}
